import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { Form, Input, Button, message, Table, Col, Row, Card, Statistic } from 'antd';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { fetchMainBudgetData } from '../../Services/dataServices';

interface RewardTypeData {
    id?: string;
    rewardTypeName: string;
    totalRewardAmount: number;
}

const RewardTypeReport: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [reportData, setReportData] = useState<RewardTypeData[]>([]);
  const [showReport, setShowReport] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [totalBudget, setTotalBudget] = useState<number>(0);
  const [balance, setBalance] = useState<number>(0);
  const [usedBudget, setUsedBudget] = useState<number>(0);

  useEffect(() => {
    fetchToken();
    getMainBudgetData();
  }, []);

  const fetchToken = async () => {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0]
    });
    return res.idToken;
  };

  const handleFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  };
    
  const handleToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  };

  const handleSubmit = async () => {
    if (!fromDate || !toDate) {
      message.error('Please fill all fields');
      return;
    }
    if (new Date(toDate) < new Date(fromDate)) {
      message.error('To-Date must be greater than From-Date');
      return;
    }
    const API_URL = process.env.REACT_APP_API_URL;

    try {
      const token = await fetchToken();
      const response = await axios.get(`${API_URL}/api/RewardsReports/reward-types-report`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        params: {
          fromDate,
          toDate,
        },
      });
      // Add unique ID to each record for stable rowKey
      const dataWithIds = response.data.map((item: RewardTypeData, index: number) => ({
        ...item,
        id: `${item.rewardTypeName}-${item.totalRewardAmount}-${index}`
      }));
      setReportData(dataWithIds);
      setShowReport(true);
      console.log('Report Data:', response.data);
    } catch (error) {
      message.error(`Failed to get report data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDownloadExcel = () => {
    if (reportData.length === 0) {
      message.error('No data available to download');
      return;
    }

    const formattedData = reportData.map((item) => ({
      'Reward Type': item.rewardTypeName,
      'Total Reward Amount': item.totalRewardAmount,
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'RewardTypeReport');

    const today = new Date();
    const formattedDate = today.toISOString().split('T')[0];

    XLSX.writeFile(workbook, `RewardTypeReport_${formattedDate}.xlsx`);
  };

  const getMainBudgetData = async () => {
    try {
      const result = await fetchMainBudgetData(instance, accounts);
      if (result && result.apiResponse) {
        const data = result.apiResponse.data;
        console.log('Main Budget Data:', data);
        setTotalBudget(data.totalBudget || 0);
        setBalance(data.balance || 0);
        setUsedBudget(data.usedBudget || 0);
        return data;
      }
    } catch (error) {
      message.error(`Failed to get main budget data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const columns = [
    {
      title: 'Reward Type Name',
      dataIndex: 'rewardTypeName',
      key: 'rewardTypeName',
      sorter: (a: RewardTypeData, b: RewardTypeData) => a.rewardTypeName.localeCompare(b.rewardTypeName),
    },
    {
      title: 'Total Reward Amount',
      dataIndex: 'totalRewardAmount',
      key: 'totalRewardAmount',
      sorter: (a: RewardTypeData, b: RewardTypeData) => a.totalRewardAmount - b.totalRewardAmount,
      render: (value: number) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    },
  ];

  return (
    <div>
      <Row gutter={12} style={{ marginBottom: '16px' }}>
        <Col span={8}>
          <Card bordered={false} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', boxShadow: '0 2px 8px rgba(102, 126, 234, 0.25)', padding: '8px 12px' }} bodyStyle={{ padding: '12px' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '12px', fontWeight: 500 }}>Total Budget</span>}
              value={totalBudget}
              precision={2}
              valueStyle={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}
              prefix="$"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', boxShadow: '0 2px 8px rgba(245, 87, 108, 0.25)', padding: '8px 12px' }} bodyStyle={{ padding: '12px' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '12px', fontWeight: 500 }}>Used Budget</span>}
              value={usedBudget}
              precision={2}
              valueStyle={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}
              prefix="$"
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card bordered={false} style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', boxShadow: '0 2px 8px rgba(79, 172, 254, 0.25)', padding: '8px 12px' }} bodyStyle={{ padding: '12px' }}>
            <Statistic
              title={<span style={{ color: '#fff', fontSize: '12px', fontWeight: 500 }}>Balance</span>}
              value={balance}
              precision={2}
              valueStyle={{ color: '#fff', fontSize: '20px', fontWeight: 'bold' }}
              prefix="$"
            />
          </Card>
        </Col>
      </Row>
      <div>
        <Form layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16} align="bottom">
            <Col span={6}>
              <Form.Item label="From Date" required>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="To Date" required>
                <Input
                  type="date"
                  value={toDate}
                  onChange={handleToDateChange}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item>
                <Button type="primary" htmlType="submit">
                  Get Report
                </Button>
                <Button type="default" onClick={handleDownloadExcel} style={{ marginLeft: '16px' }}>
                  Download as Excel
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      {showReport && (
        <div>
          <Table
            dataSource={reportData}
            columns={columns}
            rowKey="id"
            pagination={{ 
              current: currentPage,
              pageSize: pageSize,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
              onShowSizeChange: (current, size) => {
                setCurrentPage(1);
                setPageSize(size);
              }
            }}
            summary={() => {
              const totalAmount = reportData.reduce((sum, item) => sum + item.totalRewardAmount, 0);
              return (
                <Table.Summary.Row style={{ fontWeight: 'bold', backgroundColor: '#fafafa' }}>
                  <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    ${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RewardTypeReport;
